'use client';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

interface DocumentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onSave: () => void;
  isPending: boolean;
  canSave: boolean;
  dialogTitle?: string;
  dialogDescription?: string;
}

/**
 * Dialog for editing document metadata (title/description)
 *
 * @example
 * ```tsx
 * <DocumentEditDialog
 *   open={isEditing}
 *   onOpenChange={(open) => !open && closeEdit()}
 *   title={editTitle}
 *   description={editDescription}
 *   onTitleChange={setEditTitle}
 *   onDescriptionChange={setEditDescription}
 *   onSave={saveEdit}
 *   isPending={isPending}
 *   canSave={canSave}
 * />
 * ```
 */
export function DocumentEditDialog({
  open,
  onOpenChange,
  title,
  description,
  onTitleChange,
  onDescriptionChange,
  onSave,
  isPending,
  canSave,
  dialogTitle,
  dialogDescription,
}: DocumentEditDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {dialogTitle || (
              <Trans
                i18nKey="documents:editDialog.title"
                defaults="Edit Document"
              />
            )}
          </DialogTitle>
          <DialogDescription>
            {dialogDescription || (
              <Trans
                i18nKey="documents:editDialog.description"
                defaults="Update the document title and description"
              />
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-2">
          <div className="space-y-2">
            <Label htmlFor="edit-title">
              <Trans i18nKey="documents:fields.title" defaults="Title" />
            </Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              disabled={isPending}
              placeholder={t('documents:fields.titlePlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">
              <Trans
                i18nKey="documents:fields.description"
                defaults="Description (optional)"
              />
            </Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              rows={3}
              disabled={isPending}
              placeholder={t('documents:fields.descriptionPlaceholder')}
            />
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <Trans i18nKey="common:cancel" defaults="Cancel" />
          </Button>
          <Button onClick={onSave} disabled={!canSave}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans i18nKey="common:saving" defaults="Saving..." />
              </>
            ) : (
              <Trans i18nKey="common:save" defaults="Save" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
